import {ReturnInspectionPolicy} from 'types/policy';
import {ExchangeProcessingTrigger} from './useExchangeProcessingPolicy';

type Processing = {enabled: boolean; trigger: ExchangeProcessingTrigger | null};
type Issue = {kind: 'source' | 'unverified' | 'inspection' | 'matching' | 'manual' | 'canonical'; message: string; blocksSave: boolean};

export function getReviewExchangeCompatibility({inspection, inspectionChanged, inspectionSourceChanged, processing, processingChanged, processingVerified, allReturnsInspectionVerified, inspectionVerified}: {
  inspection: ReturnInspectionPolicy;
  inspectionChanged: boolean;
  inspectionSourceChanged: boolean;
  processing: Processing | null;
  processingChanged: boolean;
  processingVerified: boolean;
  allReturnsInspectionVerified: boolean;
  inspectionVerified: boolean;
}): Issue | null {
  const changed = inspectionChanged || processingChanged;
  if (inspectionSourceChanged) return {
    kind: 'source', blocksSave: changed,
    message: 'Inspection settings changed since this page loaded. Reload Review settings before changing inspection or replacement processing.',
  };
  if (!inspectionVerified) return {
    kind: inspection.enabled && inspection.mode === 'ALWAYS' ? 'canonical' : 'inspection', blocksSave: changed,
    message: inspection.enabled && inspection.mode === 'ALWAYS'
      ? 'Save a complete All returns inspection setup with manual replacement release before offering inspected customer exchanges.'
      : 'Inspection settings could not be verified. Choose an inspection option explicitly before changing replacement processing.',
  };
  if (!inspection.enabled || inspection.mode === 'NEVER') return null;
  if (!processingVerified || !processing) return changed ? {
    kind: 'unverified', blocksSave: true,
    message: 'Verify exchange processing settings before changing required inspection. Reload saved processing settings and try again.',
  } : null;
  if (inspection.mode === 'MATCHING') return {
    kind: 'matching', blocksSave: processing.enabled && changed,
    message: 'Matching returns inspection is available for refunds and store credit. Customer exchanges do not yet support this inspection mode.',
  };
  if (processing.enabled && processing.trigger !== 'MANUAL_APPROVAL') return {
    kind: 'manual', blocksSave: changed,
    message: changed
      ? 'Inspection is required. Customer exchanges need All returns inspection and manual replacement release. Choose a compatible setup before saving.'
      : 'Saved inspection and replacement settings do not support customer exchanges. Use All returns inspection and manual replacement release; unrelated review changes can still be saved.',
  };
  if (processing.enabled && !allReturnsInspectionVerified) return {
    kind: 'canonical', blocksSave: changed,
    message: 'Save a complete All returns inspection setup with manual replacement release before offering inspected customer exchanges.',
  };
  return null;
}

export function getReplacementWorkflowSummary(inspection: ReturnInspectionPolicy, processing: Processing | null, verified: boolean, approval: 'MANUAL' | 'AUTOMATIC'): string {
  if (!verified || !processing) return 'Verify exchange processing settings to see the replacement workflow.';
  if (!processing.enabled) return 'Exchange processing is off. New confirmed exchanges cannot progress until processing is enabled; customer eligibility is configured separately.';
  if (inspection.enabled && (inspection.mode !== 'ALWAYS' || processing.trigger !== 'MANUAL_APPROVAL')) return 'This inspection and replacement combination does not support new customer exchanges. Refund and store-credit settings remain separate.';
  const approvalStep = approval === 'AUTOMATIC' ? 'Eligible request approved automatically' : 'Team approves the request';
  if (inspection.enabled) return `${approvalStep} → Team receives the returned items → Full inspection approved → Team releases the replacement separately`;
  const events: Record<ExchangeProcessingTrigger, string> = {
    LABEL_CREATED: 'Return label created',
    FIRST_CARRIER_SCAN: 'First carrier scan',
    IN_TRANSIT: 'Carrier marks the return in transit (legacy)',
    DELIVERED: 'Return delivered — carrier confirmed',
    MANUAL_APPROVAL: 'Team releases the replacement manually',
  };
  return `${approvalStep} → ${processing.trigger ? events[processing.trigger] : 'Choose a processing event'} → Replacement can proceed once all checks pass`;
}
